import { css, type DefaultTheme } from 'styled-components';

// Per-token readers used by every port-pill consumer
// (PortHandles / OutputRow / BranchRow / ImageGenNodeBody). Kept here
// so the wire-mix tones resolve to identical strings everywhere; copies
// of this block used to live inline in each consumer and tripped
// SonarCloud's duplicated-lines check.
export const primaryBoldFor = (theme: DefaultTheme): string =>
  theme.colors?.primaryBold ?? '#8E8DFF';

export const outlineVariantFor = (theme: DefaultTheme): string =>
  theme.colors?.outlineVariant ?? '#25263A';

export const primaryEmphasisFor = (theme: DefaultTheme): string =>
  theme.colors?.primaryEmphasis ?? '#CFC9F5';

export const surfaceContainerHighFor = (theme: DefaultTheme): string =>
  theme.colors?.surfaceContainerHigh ?? '#1F2030';

export const outlineFor = (theme: DefaultTheme): string =>
  theme.colors?.outline ?? '#3B3D52';

export const isLightTheme = (theme: DefaultTheme): boolean =>
  !!theme.isLightTheme;

export const pillBgFor = (theme: DefaultTheme): string =>
  isLightTheme(theme)
    ? surfaceContainerHighFor(theme)
    : `color-mix(in oklch, ${primaryBoldFor(theme)} 42%, ${outlineVariantFor(theme)})`;

// Light-mode border uses the heavier `outline` token. The single-edge
// PortHandles pill prefers the lighter `outlineVariant` instead — that
// one stays inline in PortHandles.styled.ts.
export const pillBorderFor = (theme: DefaultTheme): string =>
  isLightTheme(theme)
    ? outlineFor(theme)
    : `color-mix(in oklch, ${primaryBoldFor(theme)} 50%, ${outlineVariantFor(theme)})`;

export const lavenderBorderFor = (theme: DefaultTheme): string =>
  isLightTheme(theme)
    ? `color-mix(in oklch, ${primaryBoldFor(theme)} 70%, white)`
    : `color-mix(in oklch, ${primaryEmphasisFor(theme)} 70%, ${outlineVariantFor(theme)})`;

/**
 * Shared body for the multi-output / image-output port pill — a 22×38
 * capsule that hosts the React Flow `<Handle>` and swaps tones via
 * data-state="{selected,runDone,orphan}" + data-hovered. OutputRowPill
 * and FloatingOutputPill differ only in the `right` offset that places
 * the capsule on the card's outer edge, so the rest of the recipe lives
 * here to keep them visually identical and SonarCloud happy.
 */
export const portPillBodyCss = (rightOffsetPx: number) => css`
  flex: none;
  position: absolute;
  right: ${rightOffsetPx}px;
  top: 50%;
  margin-top: -19px;
  width: 22px;
  height: 38px;
  border-radius: 999px;
  border: 1px solid ${({ theme }) => pillBorderFor(theme)};
  background: ${({ theme }) => pillBgFor(theme)};
  color: ${({ theme }) =>
    isLightTheme(theme)
      ? (theme.colors?.onPrimaryContainer ?? '#0A006B')
      : 'color-mix(in oklch, white 70%, transparent)'};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: crosshair;
  padding: 0;
  box-shadow: 0 2px 6px rgb(0 0 0 / 0.25);
  z-index: 3;
  transition:
    transform 120ms ease,
    background 120ms ease,
    border-color 120ms ease,
    box-shadow 120ms ease,
    color 120ms ease;

  & > svg {
    display: block;
    margin-left: 1px;
    pointer-events: none;
  }

  &::before {
    content: '';
    position: absolute;
    inset: -8px;
    border-radius: 999px;
    background: transparent;
  }

  &:hover {
    transform: scale(1.08);
    background: ${({ theme }) => primaryEmphasisFor(theme)};
    border-color: ${({ theme }) => lavenderBorderFor(theme)};
    color: ${({ theme }) => theme.colors?.onPrimaryEmphasis ?? '#1E1A8A'};
  }

  /* Make the React Flow Handle fill the pill so the entire pill is the
     wire-drag target. */
  & .react-flow__handle {
    position: absolute;
    inset: 0;
    width: auto !important;
    height: auto !important;
    border-radius: 999px;
    background: transparent !important;
    border: 0 !important;
    transform: none !important;
    pointer-events: auto;
    cursor: crosshair;
  }

  [data-state='selected'] &,
  [data-hovered='true'] & {
    background: ${({ theme }) => primaryEmphasisFor(theme)};
    border-color: ${({ theme }) => lavenderBorderFor(theme)};
    color: ${({ theme }) => theme.colors?.onPrimaryEmphasis ?? '#1E1A8A'};
  }

  [data-state='runDone'] & {
    background: ${({ theme }) => theme.colors?.successFixed ?? '#4ade80'};
    border-color: color-mix(
      in oklch,
      ${({ theme }) => theme.colors?.successFixed ?? '#4ade80'} 80%,
      white 15%
    );
    color: ${({ theme: { colors = {} } }) => colors.onSuccess ?? '#002106'};
  }

  [data-state='orphan'] & {
    background: color-mix(
      in oklch,
      ${({ theme }) => theme.colors?.orphanFill ?? '#F4E0A6'} 85%,
      ${({ theme }) => theme.colors?.surface ?? '#0E0F18'}
    );
    border-color: ${({ theme }) => theme.colors?.orphanBorder ?? '#E6C76A'};
    color: ${({ theme }) => theme.colors?.orphanInk ?? '#6E5210'};
  }
`;
